import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './SmoothPageTransition.scss';

/**
 * Komponent zapewniający płynne przejścia między stronami
 * - Utrzymuje poprzedni widok podczas ładowania nowej strony
 * - Zapewnia płynne animacje przejść
 * - Nie pozwala na miganie/znikanie strony podczas nawigacji
 * - Zapobiega pokazywaniu strony błędu (404 "ups") podczas ładowania
 */
const SmoothPageTransition = ({ children, loadingIndicator }) => {
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState('fadeIn');
  const [isLoading, setIsLoading] = useState(false);
  const [prevChildren, setPrevChildren] = useState(null);
  const [childrenWithoutNotFound, setChildrenWithoutNotFound] = useState(children);
  const timeoutRef = useRef(null);
  const navigationTimeoutRef = useRef(null);

  // Sprawdza, czy dzieci zawierają stronę 404 (ups)
  const containsNotFoundPage = (childrenToCheck) => {
    if (!childrenToCheck) return false;

    // Sprawdzenie, czy children zawiera komponent NotFoundPage
    const checkChildren = (child) => {
      if (!child || !child.type) return false;

      // Jeśli typ dziecka to string (np. 'div'), pomiń
      if (typeof child.type === 'string') return false;

      // Sprawdź nazwę komponentu
      if (child.type.name === 'NotFoundPage') return true;

      // Rekurencyjnie sprawdź dzieci
      if (child.props && child.props.children) {
        const nestedChildren = React.Children.toArray(child.props.children);
        return nestedChildren.some(checkChildren);
      }
      return false;
    };

    const childrenArray = React.Children.toArray(childrenToCheck);
    return childrenArray.some(checkChildren);
  };

  // Efekt sprawdzający, czy aktualny widok zawiera 404 i zachowujący poprzedni poprawny widok
  useEffect(() => {
    // Jeśli nowy widok nie zawiera 404, zaktualizuj zapisany poprawny widok
    if (!containsNotFoundPage(children)) {
      setChildrenWithoutNotFound(children);
    }
  }, [children]);

  useEffect(() => {
    // Jeśli lokalizacja zmieniła się, rozpocznij animację
    if (location.pathname !== displayLocation.pathname) {
      // Zapisz aktualny widok
      if (!containsNotFoundPage(children)) {
        setPrevChildren(children);
      } else {
        // Jeśli aktualny widok to 404, użyj ostatniego poprawnego widoku
        setPrevChildren(childrenWithoutNotFound);
      }
      
      // Rozpocznij animację wyjścia
      setTransitionStage('fadeOut');
      setIsLoading(true);
      
      // Po zakończeniu animacji wyjścia, zaktualizuj lokalizację
      timeoutRef.current = setTimeout(() => {
        setDisplayLocation(location);
        
        // Rozpocznij animację wejścia po krótkim opóźnieniu
        setTimeout(() => {
          setTransitionStage('fadeIn');
          
          // Jeśli dzieci nie zawierają 404, zakończ ładowanie
          if (!containsNotFoundPage(children)) {
            setIsLoading(false);
          } else {
            // Jeśli to 404, poczekaj dłużej (może dane jeszcze dotarły)
            navigationTimeoutRef.current = setTimeout(() => {
              setIsLoading(false);
            }, 2000); // Dajmy API więcej czasu na odpowiedź
          }
        }, 50);
      }, 300); // Czas trwania animacji wyjścia
    }
    
    // Czyszczenie timeoutów przy odmontowaniu
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (navigationTimeoutRef.current) clearTimeout(navigationTimeoutRef.current);
    };
  }, [location, displayLocation, children, childrenWithoutNotFound]);

  // Renderowanie
  const renderChildren = () => {
    // Podczas normalnej nawigacji
    if (location.pathname === displayLocation.pathname) {
      // Jeśli dzieci zawierają 404, a jesteśmy w trakcie ładowania, pokaż ostatni poprawny widok
      if (containsNotFoundPage(children) && isLoading) {
        return childrenWithoutNotFound;
      }
      // W przeciwnym razie pokaż aktualne dzieci
      return children;
    }
    
    // Podczas przejścia pokaż poprzedni widok
    return prevChildren;
  };

  return (
    <div className="smooth-page-transition-container">
      <div className={`page-content ${transitionStage}`}>
        {/* Renderuj odpowiedni widok w zależności od sytuacji */}
        {renderChildren()}
      </div>
      
      {/* Wskaźnik ładowania */}
      {isLoading && loadingIndicator && (
        <div className="loading-indicator-overlay">
          {loadingIndicator}
        </div>
      )}
    </div>
  );
};

export default SmoothPageTransition;